// @flow
import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { Col, Row, Container, config, Hidden } from 'react-awesome-styled-grid'
import LinkifyText from '@components/LinkifyText'
import { CTAPrimaryOrange } from '@components/shared'
import Link from 'next/link'
import BackgroundGradient from '@components/BackgroundGradient'
import { useSelector } from 'react-redux'
import type { AppStateType } from '@reducers/appstate'

type QuestionType = {
  title: string,
  answer: string
}

const questions: QuestionType[] = [
  {
    title: 'What is Dipsea?',
    answer: 'Dipsea is the app for spicy audiobooks and more, featuring your favorite tropes and irresistible characters. Crafted by a team of professional writers and top-tier narrators, it’s next gen romance created by and for women.'
  },
  {
    title: 'What can I listen to with a subscription?',
    answer: 'When you sign up for a subscription, you’ll get access to the entire Dipsea library including new releases every week. Choose an annual subscription and explore 1000+ spicy audiobooks, romance novels, sleep scenes, and steamy sounds.'
  },
  {
    title: 'How often is there new content?',
    answer: 'New content is released each week, and with hundreds of spicy audiobooks and more to explore, there\'s always something exciting to discover.'
  },
  {
    title: '30-Day Money Back Guarantee',
    answer: 'We stand behind everything we make. If you are not satisfied with Dipsea Unlimited, you can get a refund— no questions asked—within 30 days of the purchase date. Just log into your account -> manage subscription and click refund. No support conversation needed.'
  }
]

const CloseCircleSVG = (): React.Node => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M38.6666 20C38.6666 30.3093 30.3092 38.6666 19.9999 38.6666C9.6906 38.6666 1.33325 30.3093 1.33325 20C1.33325 9.69066 9.6906 1.33331 19.9999 1.33331C30.3092 1.33331 38.6666 9.69066 38.6666 20Z"
      stroke={colors.white} strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M27.6663 20L12.3329 20" stroke={colors.white} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>)

const OpenCircleSVG = (): React.Node => (
  <svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M38.6666 20.5C38.6666 30.8094 30.3092 39.1667 19.9999 39.1667C9.6906 39.1667 1.33325 30.8094 1.33325 20.5C1.33325 10.1907 9.6906 1.83337 19.9999 1.83337C30.3092 1.83337 38.6666 10.1907 38.6666 20.5Z"
      stroke={colors.white} strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 28.1663L20 12.833" stroke={colors.white} strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M27.6663 20.5001L12.3329 20.5001" stroke={colors.white} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>)
export default function SubscribeFaqs (props: {setShowCreateAccount: (show: boolean) => void}): React.Node {
  const subscriptionCta = useSelector<string>((state: AppStateType): string => state.subscriptionCTA)
  return (
    <Section>
      <BackgroundGradient quadrant={3} overlayColor='magenta' />
      <FaqsContainer>
        <Container>
          <Row>
            <Col align="center" xl={8} lg={8} md={6} sm={8} xs={4} offset={{
              xl: 2,
              lg: 2,
              md: 1
            }}>
              <Subheader>Why Dipsea?</Subheader>
            </Col>
          </Row>
          <Row>
            <Col xl={8} lg={8} md={6} sm={8} xs={4} offset={{
              xl: 2,
              lg: 2,
              md: 1
            }}>
              <Hr size={1} />
            </Col>
          </Row>
          {questions.map((item: QuestionType, index: number): React.Node => {
            return (
              <Question answer={item.answer} title={item.title} key={`question-${index}`} />
            )
          })}
        </Container>
        <Row>
          <Col xl={8} lg={8} md={6} sm={8} xs={4} offset={{
            xl: 2,
            lg: 2,
            md: 1
          }}
            align="center"
          >
            <CtaContainer>
              <CTAPrimaryOrange className='subscribe-cta' onClick={(): void => props.setShowCreateAccount(true)}>{subscriptionCta}</CTAPrimaryOrange>
              <div>
                Or <Link href={'/discover'}>explore what we offer</Link>
              </div>
            </CtaContainer>
          </Col>
        </Row>
      </FaqsContainer>
    </Section>
  )
}

function Question ({ answer, title }: QuestionType): React.Node {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  return (
    <QuestionContainer>
      <Row align={'center'}>
        <Hidden xs={true} sm={true}>
          <Col xl={7} lg={7} md={5} xs={4} offset={{
            xl: 2,
            lg: 2,
            md: 1
          }} align="flex-start">
            <H4 color={colors.white} onClick={(): void => setIsOpen(!isOpen)}
              style={{ cursor: 'pointer' }}>{title}</H4>
            {isOpen && <QuestionAnswer><LinkifyText>{answer}</LinkifyText></QuestionAnswer>}
          </Col>
          <Col xl={1} lg={1} md={1} align={'flex-end'} onClick={(): void => setIsOpen(!isOpen)}
            style={{ cursor: 'pointer' }}>{
              isOpen ? <CloseCircleSVG/> : <OpenCircleSVG/>
            }</Col>
        </Hidden>
        <Hidden xl={true} lg={true} md={true} >
          <Col xs={3} align="flex-start" onClick={(): void => setIsOpen(!isOpen)}>
            <H4 color={colors.white} style={{ cursor: 'pointer' }}>{title}</H4>
          </Col>
          <Col xs={1} align="flex-end" onClick={(): void => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }}>
            {
              isOpen ? <CloseCircleSVG/> : <OpenCircleSVG/>
            }
          </Col>
          {isOpen && <Col xs={4}><QuestionAnswer><LinkifyText>{answer}</LinkifyText></QuestionAnswer></Col>}
        </Hidden>
        <Col xl={8} lg={8} md={6} sm={8} xs={4} offset={{
          xl: 2,
          lg: 2,
          md: 1
        }}>
          <Hr/>
        </Col>
      </Row>
    </QuestionContainer>
  )
}
const Section = styled.section`
  position: relative;
`
const CtaContainer = styled.div`
    width: 330px;
    margin-top: 40px;
    div {
        margin-top: 12px;
        width: 100%;
        text-align: center;
        font-family: "Good Sans Regular";
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        color: ${colors.white};
        a {
            color: ${colors.white};
        }
    }
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`
const H4 = styled.h4`
    font-family: "Queens Condensed Light";
    font-size: 23px;
    font-style: normal;
    font-weight: 300;
    line-height: 110%;
    color: ${colors.white};
    margin: 0;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
    font-size: 28px;
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`
const Subheader = styled.div`
  font-family: "Queens Condensed Light";
  font-size: 45px;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
  color: ${colors.white};
  margin-bottom: 24px;

`
const Hr = styled.hr`
  width: 100%;
  height: 1px;
  border: none;
  background-color: ${colors.white};
  margin-top: 12px;
  margin-bottom: 12px;
`
const QuestionContainer = styled.div``
const FaqsContainer = styled.div`
    position: relative;
    overflow: hidden;
    padding-top: 60px;
    padding-bottom: 80px;
    z-index: 1;
`

const QuestionAnswer = styled.div`
  font-family: "Good Sans Regular";
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  margin-top: 7px;
  color: ${colors.white};
  ${(props: any): string => config(props).media.sm`
    margin-top: 10px;
    margin-bottom: 10px;
  `}
  a {
    color: ${colors.orange};
    font-family: system-ui;
  }

`
